import { Chat, Message, MessageRoleEnum } from '@hello-pimster/pimster-crm-sdk';
import { useApiCrm } from '@lib/pimster-crm/provider/ApiCrmProvider';
import { ServerEvent } from '@lib/pimster-crm/utils/eventSourceFactory';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ChangeEvent, useCallback, useReducer, useState } from 'react';
import { lastValueFrom } from 'rxjs';

import { MessagesActionType, messagesReducer } from '../utils/messagesReducer';

export type UseChatParams = {
    agentId: number;
    chatId?: string;
    locale: string;
    initialMessage: Message;
    initialInput?: string;
    onResponse?: (response: Response) => void;
    onFinish?: (event: ServerEvent<Message>) => void;
    onError?: (err: unknown) => void;
};

export const useChat = ({
    agentId,
    chatId,
    locale,
    initialMessage,
    initialInput = '',
    onResponse,
    onFinish,
    onError,
}: UseChatParams) => {
    const { client: axiosClient, fetchClient } = useApiCrm();
    const [chat, setChat] = useState<Chat>();
    const [messages, messagesDispatch] = useReducer(messagesReducer, [
        initialMessage,
    ]);
    const [input, setInput] = useState(initialInput);

    /**
     * QUERIES
     */
    /**  Chat Query  */
    useQuery<Chat>({
        retry: false,
        enabled: !chat?.id,
        queryKey: ['chat'],
        queryFn: async () => {
            try {
                const res = await (chatId
                    ? // @ts-ignore
                      axiosClient.chatControllerGetChat({ id: chatId })
                    : axiosClient.chatControllerCreateChat({
                          createChat: {
                              locale,
                              messages: [],
                              agent: { id: agentId },
                          },
                      }));

                if (res.data.agentId !== agentId) {
                    throw new Error('[PIMSTER_AI] Invalid Chat Agent');
                }
                if (res.data.messages?.length) {
                    messagesDispatch({
                        type: MessagesActionType.SET,
                        payload: res.data.messages,
                    });
                }
                setChat(res.data);

                return res.data;
            } catch (err) {
                onError?.(err);
                throw err;
            }
        },
    });
    /**  Completion Mutation  */
    const { mutate: sendUserMessage } = useMutation({
        onError,
        mutationFn: async ({
            userMessage,
            chat,
        }: {
            userMessage: Message;
            chat: Chat;
        }) => {
            const sendMessage = await fetchClient.agentControllerCompletion(
                userMessage.content,
                chat.agentId,
                // @ts-ignore
                chat.id,
                onResponse
            );

            sendMessage.subscribe({
                next: (event) => {
                    messagesDispatch({
                        type: MessagesActionType.UPSERT,
                        payload: event.data,
                    });
                },
                error: (err) => onError?.(err),
            });

            const lasServerEvent = await lastValueFrom(sendMessage);

            onFinish?.(lasServerEvent);

            return lasServerEvent.data;
        },
    });

    /**
     * FUNCTIONS
     */
    const handleSubmit = useCallback(
        async (event?: React.FormEvent<HTMLFormElement>) => {
            if (input === '') return;

            event?.preventDefault();

            if (!chat?.id) {
                throw Error(
                    '[PIMSTER_AI] Chat instance needed to perform messages mutation'
                );
            }

            const userMessage = {
                content: input,
                role: MessageRoleEnum.User,
                id: Date.now(),
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
            };

            setInput('');
            sendUserMessage({ userMessage, chat });
        },
        [input, chat, setInput, sendUserMessage]
    );

    const handleInputChange = useCallback(
        (e: ChangeEvent<HTMLTextAreaElement>) => {
            setInput(e.target.value);
        },
        []
    );

    return {
        messages,
        input,
        handleSubmit,
        handleInputChange,
    };
};
